import { useState } from "react";
import { Box, Button, Chip, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CheckCircle, FolderOutlined, RadioButtonUnchecked, ShieldOutlined } from "@mui/icons-material";
import { ProgressBar } from "@components/ProgressBar";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import useTofuSteps from "@hooks/useTofuSteps";
import { useNavigate } from "react-router-dom";
// import { RootState, useAppDispatch } from "@store/store";
// import useInterfaceConfig from "@hooks/useInterfaceConfig";
// import { useSelector } from "react-redux";
import { buildProductRoute } from "@helpers/funciones";

const SCREEN_STATUS = "CSPDI";
const SAT_OPTION = "sat";
const DOCUMENTS_OPTION = "documents";

type DecisionOption = typeof SAT_OPTION | typeof DOCUMENTS_OPTION;

interface OptionItem {
  value: DecisionOption;
  title: string;
  description: string;
  icon: typeof ShieldOutlined;
  badge?: string;
  documents?: string[];
}

const options: OptionItem[] = [
  {
    value: SAT_OPTION,
    title: "Conectar con el SAT",
    badge: "Recomendada",
    description:
      "Usa tu Contraseña SAT (antes CIEC). Mediante tu RFC más tu clave alfanumérica de 8 caracteres descargamos tu historial fiscal automáticamente con acceso de solo lectura. Tu contraseña nunca se almacena.",
    icon: ShieldOutlined,
  },
  {
    value: DOCUMENTS_OPTION,
    title: "Cargar documentos",
    description: "Sube estos 4 documentos de forma manual:",
    icon: FolderOutlined,
    documents: [
      "Constancia de Situación Fiscal (SAT, ejercicio en curso)",
      "Estados financieros internos parciales (año en curso)",
      "Estados financieros del último ejercicio fiscal",
      "3 estados de cuenta bancarios (últimos 3 meses)",
    ],
  },
];

const DesicionCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  // const dispatch = useAppDispatch();
  // const interfaceConfig = useInterfaceConfig();

  // const applicationData = useSelector((state: RootState) => state.register.application);

  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const [selectedOption, setSelectedOption] = useState<DecisionOption>(SAT_OPTION);

  const handleOnClickContinue = () => {
    const path = selectedOption === SAT_OPTION ? "consulta_ciec" : "documentos_sat";
    navigate(buildProductRoute(location.pathname, path));
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box maxWidth="sm" width="100%">
            <Typography variant="overline" fontWeight={700} letterSpacing={1.6} color="primary.main">
              DECISIÓN
            </Typography>

            <Typography variant="h3" color="primary.dark" fontWeight={700} mt={1}>
              ¿Cómo quieres declarar tus ingresos?
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
              Elige la vía con la que compartirás la información financiera de tu empresa.
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, px: "0 !important" }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: 3, mb: 2, px: "0 !important" }}>
            <Box display="flex" flexDirection="column" gap={2}>
              {options.map((option) => {
                const isSelected = selectedOption === option.value;
                const Icon = option.icon;

                return (
                  <Box
                    key={option.value}
                    onClick={() => setSelectedOption(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedOption(option.value);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    sx={{
                      p: isMobileOrTablet ? 2 : 2.5,
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "grey.300",
                      borderRadius: 3,
                      cursor: "pointer",
                      backgroundColor: isSelected ? "rgba(82, 140, 214, 0.06)" : "common.white",
                      boxShadow: isSelected ? "0px 0px 0px 1px rgba(82, 140, 214, 0.12)" : "none",
                      transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Box
                        sx={{
                          width: isMobileOrTablet ? 40 : 44,
                          height: isMobileOrTablet ? 40 : 44,
                          minWidth: isMobileOrTablet ? 40 : 44,
                          borderRadius: 1.5,
                          backgroundColor: isSelected ? "primary.main" : "grey.100",
                          color: isSelected ? "common.white" : "primary.dark",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>

                      <Box flex={1}>
                        <Box
                          display="flex"
                          alignItems={isMobileOrTablet ? "flex-start" : "center"}
                          justifyContent="space-between"
                          gap={1.5}
                          flexDirection={isMobileOrTablet ? "column" : "row"}
                        >
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography
                              variant={isMobileOrTablet ? "body1" : "h6"}
                              fontWeight={600}
                              color="primary.dark"
                            >
                              {option.title}
                            </Typography>

                            {option.badge && (
                              <Chip
                                label={option.badge}
                                size="small"
                                sx={{
                                  height: 24,
                                  backgroundColor: "success.light",
                                  color: "success.dark",
                                  fontWeight: 600,
                                  "& .MuiChip-label": {
                                    px: 1.25,
                                  },
                                }}
                              />
                            )}
                          </Box>

                          {isSelected ? (
                            <CheckCircle sx={{ color: "primary.main", fontSize: 26 }} />
                          ) : (
                            <RadioButtonUnchecked sx={{ color: "grey.400", fontSize: 26 }} />
                          )}
                        </Box>

                        <Typography
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          mt={1}
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {option.description}
                        </Typography>

                        {option.documents && (
                          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, color: "text.secondary" }}>
                            {option.documents.map((document) => (
                              <Box
                                key={document}
                                component="li"
                                sx={{
                                  mb: 0.5,
                                  typography: isMobileOrTablet ? "body2" : "body1",
                                }}
                              >
                                {document}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Container>

          <Container maxWidth="sm" sx={{ mb: 2, px: "0 !important" }}>
            <Box display="flex" justifyContent="center">
              <Button variant="default" color="primary" disabled={!selectedOption} onClick={handleOnClickContinue}>
                Continuar
              </Button>
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DesicionCiec;
